import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { contactUs } from "../services/contactService";
import { Button, Input, message } from "antd";
import { ArrowLeftOutlined, SendOutlined, MessageOutlined } from "@ant-design/icons";
import ContactSuccessModal from "../components/modals/ContactSuccessModal";

function ContactUs() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      message: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      message: Yup.string().required("Message is required"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setIsLoading(true);
      try {
        console.log(values);
        await contactUs(values);
        resetForm();
        setShowSuccessModal(true);
      } catch (error) {
        message.error(error.response?.data?.message || "Something went wrong");
        resetForm();
      } finally {
        setSubmitting(false);
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    const storedEmail = localStorage.getItem("email") || "";
    if (storedEmail) {
      formik.setFieldValue("email", storedEmail);
    }
  }, []);

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setTimeout(() => {
      navigate("/chat");
    }, 200);
  };

  return (
    <>
      <div className="w-full min-h-screen flex justify-center items-center py-[60px] px-4 bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="flex flex-col w-full max-w-[600px] gap-[32px] py-[48px] md:py-[64px] px-[32px] md:px-[56px] shadow-xl bg-white rounded-[24px] relative border border-gray-100">
          {/* Back Button */}
          <button
            onClick={() => navigate("/chat")}
            className="absolute top-6 left-6 flex items-center gap-2 text-gray-600 hover:text-[#E61F26] transition-all duration-200 font-Poppins font-[500] text-[14px] group"
          >
            <ArrowLeftOutlined className="text-[16px] group-hover:-translate-x-1 transition-transform duration-200" />
            <span className="hidden sm:inline">Back to Chat</span>
          </button>

          {/* Header Section */}
          <div className="flex flex-col gap-[16px] items-center mt-8 md:mt-0">
            <div className="w-16 h-16 bg-gradient-to-br from-[#E61F26] to-[#c71920] rounded-full flex items-center justify-center shadow-lg">
              <MessageOutlined className="text-[32px] text-white" />
            </div>
            <div className="flex flex-col gap-[8px]">
              <h2 className="font-Poppins text-[32px] md:text-[36px] leading-[40px] md:leading-[44px] font-[700] text-center text-[#010101]">
                Get in Touch
              </h2>
              <p className="font-Poppins font-[400] text-[15px] md:text-[16px] leading-[24px] text-center text-[#667085] max-w-[400px]">
                Have a question or feedback? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>
            </div>
          </div>

          {/* Form Section */}
          <form
            onSubmit={formik.handleSubmit}
            className="flex w-full flex-col gap-[24px]"
          >
            <div className="flex flex-col gap-[10px]">
              <label
                className="text-[#344054] font-Poppins text-[14px] font-[600] leading-[20px] flex items-center gap-2"
                htmlFor="name"
              >
                Full Name
                <span className="text-[#E61F26]">*</span>
              </label>
              <Input
                className={`py-[14px] px-[18px] rounded-[12px] border-[1.5px] font-Poppins text-[15px] transition-all hover:border-[#E61F26] ${
                  formik.touched.name && formik.errors.name
                    ? "border-red-500 focus:border-red-500"
                    : "border-[#D0D5DD] focus:border-[#E61F26]"
                }`}
                id="name"
                name="name"
                type="text"
                placeholder="John Doe"
                {...formik.getFieldProps("name")}
              />
              {formik.touched.name && formik.errors.name ? (
                <div className="text-red-500 text-[13px] font-Poppins font-[500] mt-1">
                  {formik.errors.name}
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-[10px]">
              <label
                className="text-[#344054] font-Poppins text-[14px] font-[600] leading-[20px] flex items-center gap-2"
                htmlFor="message"
              >
                Your Message
                <span className="text-[#E61F26]">*</span>
              </label>
              <Input.TextArea
                style={{ resize: "none" }}
                rows={6}
                className={`py-[14px] px-[18px] rounded-[12px] border-[1.5px] font-Poppins text-[15px] transition-all hover:border-[#E61F26] ${
                  formik.touched.message && formik.errors.message
                    ? "border-red-500 focus:border-red-500"
                    : "border-[#D0D5DD] focus:border-[#E61F26]"
                }`}
                id="message"
                name="message"
                placeholder="Tell us what's on your mind..."
                {...formik.getFieldProps("message")}
              />
              {formik.touched.message && formik.errors.message ? (
                <div className="text-red-500 text-[13px] font-Poppins font-[500] mt-1">
                  {formik.errors.message}
                </div>
              ) : null}
            </div>

            <div className="flex w-full mt-4">
              <Button
                type="primary"
                htmlType="submit"
                disabled={formik.isSubmitting || isLoading}
                className="bg-gradient-to-r from-[#E61F26] to-[#c71920] hover:from-[#c71920] hover:to-[#a01619] h-[52px] w-full font-Poppins font-[600] text-[16px] rounded-[12px] transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                icon={!isLoading && <SendOutlined />}
              >
                {isLoading ? "Sending..." : "Send Message"}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <ContactSuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseModal}
      />
    </>
  );
}

export default ContactUs;